import { useIntl } from 'react-intl'
import * as Sentry from '@sentry/browser'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCustomerFirstName,
  selectCustomerIsLoanOfficer,
  selectCustomerIsRealEstateAgent,
  selectCustomerPhotoUri,
  selectIsSoloInsuranceAgent,
  selectIsSoloRea
} from '../../../../../../../../store/selectors/customerProfile'
import {
  selectScheduleTourDate,
  setScheduleTourDataToInitialState,
  selectScheduleTourStep,
  setScheduleTourShowModal,
  setScheduleTourStep,
  STEPS,
  STEPS_CONST,
  STEPS_REA,
  selectScheduleTourPreferredAgent
} from '../../../../../../../../store/slices/scheduleTour'
import { Text, Avatar, ButtonGroup, Button, useToast } from '@homebotapp/hb-react-component-catalog'
import Step from '../../../../../ModalStepsOutline'
import { Listing, MessageTopicKey } from '../../../../../../../../api/gqlaxy/gql/generated/graphql'

import { useGetPreferredAgent } from '../../../../../../../../hooks/listings/useGetPreferredAgent'
import { selectClientId } from '../../../../../../../../store/selectors/client'
import {
  selectSponsorshipFullName,
  selectSponsorshipPhotoUri
} from '../../../../../../../../store/selectors/sponsorship'
import { flashNotification } from '../../../../../../../../actions/notification'
import { useSendListingMessage } from '../../../../../../../../hooks/listings/useSendListingMessage'
import { AvatarBubbles } from '../../../../../../../shared/AvatarBubbles/AvatarBubbles'

const MSG = {
  scheduleTourConfirmation: {
    id: 'ScheduleTour.confirmation.header',
    defaultMessage: 'Tour request sent!'
  },
  inTheMeantime: {
    id: 'ScheduleTour.confirmation.inTheMeantime',
    defaultMessage: 'In the meantime, would you like to get pre-qualified?'
  },
  inTheMeantimeSponsored: {
    id: 'ScheduleTour.confirmation.inTheMeantimeSponsored',
    defaultMessage: 'Would you like to get pre-qualified by my trusted lending partner, {sponsorName} in the meantime?'
  },
  prequalDetails: {
    id: 'ScheduleTour.confirmation.prequalDetails',
    defaultMessage:
      'Pre-qualifying gives you a clear budget with no obligation to proceed, and makes you stand out as a serious buyer.'
  },
  willFollowUp: {
    id: 'ScheduleTour.confirmation.willFollowUp',
    defaultMessage:
      '{customerFirstName} will follow up to finalize a date and time. If you have any questions, please contact them.'
  },
  backToListing: {
    id: 'ScheduleTour.FooterButton.BackToListing',
    defaultMessage: 'Back to Listing'
  },
  getPreQualified: {
    id: 'ScheduleTour.FooterButton.GetPreQualified',
    defaultMessage: 'Get Pre-Qualified'
  },
  success: {
    id: 'ScheduleTour.FooterButton.Success',
    defaultMessage: 'Pre-qualification request sent!'
  },
  fail: {
    id: 'ScheduleTour.FooterButton.Fail',
    defaultMessage: 'Pre-qualification request failed to send.'
  }
}

export interface ConfirmationProps {
  listing: Listing
}

export const Confirmation = ({ listing }: ConfirmationProps) => {
  const currentStep = useSelector(selectScheduleTourStep)
  const stepIndex = STEPS.indexOf(currentStep)
  const tourDate = useSelector(selectScheduleTourDate)
  const isCustomerREA = useSelector(selectCustomerIsRealEstateAgent)
  const isCustomerLo = useSelector(selectCustomerIsLoanOfficer)
  const sponsorName = useSelector(selectSponsorshipFullName)
  const agentPreference = useSelector(selectScheduleTourPreferredAgent)
  const isSoloRea = useSelector(selectIsSoloRea)
  const isSoloInsuranceAgent = useSelector(selectIsSoloInsuranceAgent)
  const toast = useToast()
  const handleModalClose = () => {
    dispatch(setScheduleTourStep(STEPS[0]))
    dispatch(setScheduleTourShowModal(false))
  }

  const hidePrequalElements = isSoloRea || isSoloInsuranceAgent

  const clientId = useSelector(selectClientId) as string
  const { data: preferredAgentData } = useGetPreferredAgent(
    {
      input: {
        clientId
      }
    },
    {
      enabled: isCustomerLo
    }
  )

  const preferredAgent = preferredAgentData?.preferredAgent
  const customerFirstName = useSelector(selectCustomerFirstName)

  let agentFirstName = listing?.agent?.name
  if (agentPreference === 'preferredAgent' && preferredAgent?.agentFirstName) {
    agentFirstName = preferredAgent?.agentFirstName
  }
  const sponsorPhoto = useSelector(selectSponsorshipPhotoUri)
  const customerPhoto = useSelector(selectCustomerPhotoUri)

  const dispatch = useDispatch()
  const intl = useIntl()

  const { mutateAsync: sendListingMessage } = useSendListingMessage()

  const handleSubmit = async () => {
    try {
      await sendListingMessage({
        input: {
          message: 'I want to get pre-qualified for this home.',
          topicKey: MessageTopicKey.ListingsPrequalRequest,
          listingId: listing?.id
        }
      })
      toast({
        title: intl.formatMessage(MSG.success),
        status: 'success'
      })
      handleModalClose()
    } catch (err: any) {
      Sentry.captureMessage('DM failed to send', {
        extra: {
          error: err.message || '',
          requestData: err.config?.data || {},
          requestErrors: Object.values(err.response?.data?.errors) || []
        }
      })
      dispatch(flashNotification({ message: intl.formatMessage(MSG.fail), messageType: 'error' }))
    }
  }

  return (
    <Step stepLabel='schedule-tour-confirmation'>
      <Step.HeaderNavSection
        currentStep={currentStep}
        steps={isCustomerREA ? STEPS_REA : STEPS}
        onCancel={() => dispatch(setScheduleTourShowModal(false))}
        onBack={() => dispatch(setScheduleTourStep(STEPS_CONST[stepIndex - 1]))}
      >
        <Text fontWeight='bold'>✅ {intl.formatMessage(MSG.scheduleTourConfirmation)}</Text>
      </Step.HeaderNavSection>
      <Step.Body>
        <Text color='neutral.900'>
          {intl.formatMessage(MSG.willFollowUp, {
            customerFirstName: isCustomerREA ? customerFirstName : agentFirstName
          })}
        </Text>

        {!hidePrequalElements && (
          <>
            <AvatarBubbles photoUri={customerPhoto} companyLogo={sponsorPhoto} />
            <Text fontWeight='bold' color='neutral.900' ml={3} mb={3}>
              {sponsorName
                ? intl.formatMessage(MSG.inTheMeantimeSponsored, { sponsorName: sponsorName })
                : intl.formatMessage(MSG.inTheMeantime)}
            </Text>
            <Text color='neutral.900'>{intl.formatMessage(MSG.prequalDetails)}</Text>
          </>
        )}
      </Step.Body>
      <ButtonGroup>
        <Button
          colorScheme='secondary'
          onClick={() => {
            dispatch(setScheduleTourDataToInitialState())
          }}
          tracking={{
            descriptor: 'Back to Listing button',
            guid: 'sWPqtsFHAucN2569Ci9xWY',
            ui_context: 'Listing Details Schedule Tour Confirmation'
          }}
        >
          {intl.formatMessage(MSG.backToListing)}
        </Button>
        {!hidePrequalElements && (
          <Button
            colorScheme='primary'
            onClick={handleSubmit}
            tracking={{
              descriptor: 'Get Pre-Qualified button',
              guid: 'fJhdoqKt1pJPesg4gnUKeM',
              ui_context: 'Listing Details Schedule Tour Confirmation'
            }}
          >
            {intl.formatMessage(MSG.getPreQualified)}
          </Button>
        )}
      </ButtonGroup>
    </Step>
  )
}
